import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, User, X } from 'lucide-react';
import { ProjectList } from '../projects/ProjectList';
import { useNavigate } from 'react-router-dom';
import { APP_NAME } from '../../constants/app';
import NotificationCenter from '../notifications/NotificationCenter';
import { animated } from 'react-spring';
import { useSwipeDrawer } from '../../hooks/useSwipeDrawer';
import { useUser } from '../../hooks/queries/useAuth';
import { getUserAvatarUrl } from '../../lib/user-utils';
import { ProfileMenu } from '../profile/ProfileMenu';
import { useViewport } from '../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onProjectSelect?: (id: string) => void;
  showBackButton?: boolean;
  onBack?: () => void;
}

export function MobileDrawer({
  isOpen,
  onClose,
  projectId,
  onProjectSelect,
  showBackButton = false,
  onBack,
}: MobileDrawerProps) {
  const navigate = useNavigate();
  const { data: user } = useUser();
  const avatarUrl = getUserAvatarUrl(user);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const avatarRef = useRef<HTMLButtonElement>(null);
  const { isMobile } = useViewport();
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();

  // Use our swipe hook with full screen width
  const { x, bind } = useSwipeDrawer({
    isOpen,
    setIsOpen: open => {
      if (!open) onClose();
    },
    maxWidth: window.innerWidth, // Use full screen width
  });

  // Add resize listener to handle orientation changes
  useEffect(() => {
    // Force redraw when window is resized (particularly for orientation changes)
    const handleResize = () => {
      if (!isOpen) {
        // If drawer is closed, ensure it's positioned correctly off-screen
        window.requestAnimationFrame(() => {
          const drawerEl = document.querySelector('.mobile-drawer');
          if (drawerEl instanceof HTMLElement) {
            drawerEl.style.transform = `translateX(-${window.innerWidth}px)`;
          }
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  const handleProjectSelect = (id: string) => {
    // If the project is already selected, just close the drawer
    if (id === projectId) {
      onClose();
      return;
    }

    if (onProjectSelect) {
      onProjectSelect(id);
    } else {
      navigate(`/project/${id}`);
    }
    onClose();
  };

  const handleDashboardClick = () => {
    navigate('/dashboard');
    onClose();
  };

  const handleAvatarClick = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  return (
    <>
      {/* Overlay with animated opacity - removed backdrop-blur-sm that was causing issues on orientation change */}
      <animated.div
        className="fixed inset-0 bg-black/50 z-[100]"
        onClick={onClose}
        style={{
          opacity: x.to({
            range: [-window.innerWidth, 0],
            output: [0, 1],
          }),
          // Hide overlay completely when drawer is closed
          pointerEvents: isOpen ? 'auto' : 'none',
          visibility: isOpen ? 'visible' : 'hidden', // Ensure it's fully hidden when not open
          WebkitTapHighlightColor: 'transparent',
        }}
      />

      {/* Drawer - now using react-spring for animation and full width */}
      <animated.div
        {...bind()}
        className="fixed inset-y-0 left-0 z-[110] w-full bg-[var(--sidebar-bg-color)] flex flex-col touch-none shadow-lg mobile-drawer"
        style={{
          transform: x.to(x => `translateX(${x}px)`),
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {/* Custom Header with Logo, Notification Center, and Close Button all on one line */}
        <div
          className="p-4 flex items-center justify-between w-full"
          style={{
            paddingTop: isMobile && isNativeApp ? `${statusBarHeight + 16}px` : '1rem',
            height: 'auto',
          }}
        >
          {showBackButton && onBack ? (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft size={20} />
              <span>Back</span>
            </button>
          ) : (
            <button
              onClick={handleDashboardClick}
              className="hover:opacity-80 transition-opacity flex items-center gap-2"
            >
              <img
                src="/logo-icon-white.svg"
                alt="Zentime Logo"
                className="h-10 w-auto transition-all duration-200"
                style={{ transform: 'translate(-2px, 1px)' }}
              />
              <h1
                className="text-xl font-bold font-['Sora']"
                style={{ color: 'var(--text-primary)' }}
              >
                {APP_NAME}
              </h1>
            </button>
          )}

          <div className="flex items-center space-x-2">
            {/* User Avatar - no border, just shadow */}
            <button
              ref={avatarRef}
              onClick={handleAvatarClick}
              className="w-8 h-8 mr-2 rounded-full bg-gray-700 flex-shrink-0 overflow-hidden shadow-md flex items-center justify-center"
              aria-label="User menu"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={e => {
                    // Fallback to User icon if image fails to load
                    const fallbackIcon = e.currentTarget.parentElement?.querySelector('div');
                    if (fallbackIcon) {
                      e.currentTarget.style.display = 'none';
                      fallbackIcon.style.display = 'flex';
                    }
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User size={16} className="text-gray-300" />
                </div>
              )}
            </button>

            {/* Profile Menu */}
            <ProfileMenu
              isOpen={isProfileMenuOpen}
              onClose={() => setIsProfileMenuOpen(false)}
              triggerRef={avatarRef}
            />

            {/* Notification Center - matching hover style with close button */}
            <div className="notification-wrapper">
              <NotificationCenter />
            </div>

            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Project List */}
        <div className="flex-1 overflow-y-auto text-[var(--text-primary)]">
          <ProjectList
            activeProjectId={projectId}
            onProjectSelect={handleProjectSelect}
            hideHeader={true}
          />
        </div>
      </animated.div>
    </>
  );
}
